import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Tabs,
  Tab,
  Stack,
  InputAdornment,
} from "@mui/material";
import { Delete, Edit, Save, Close, Refresh, Add, Search, Clear } from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { usePageTitle } from "contexts/PageTitleContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
import AppPagination from "components/AppPagination";
import SearchInput from "components/input-fields/SearchInput";
import FlexBox from "components/flexbox/FlexBox";
import AppPaginationBar from "components/AppPaginationBar";

// ─── STEP 1: Import the config file ───────────────────────────────────────────
import buttonConfig from "../../config/ConfigAccessButton";

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "12px 16px",
  "&.header": {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[200],
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TableCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Muted = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const DiseaseAdvisory = () => {
  const theme = useTheme();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  const { showDeleteConfirmation } = useDeleteConfirmation();

  // ─── STEP 2: Set up permission helpers per sub-section ──────────────────────
  //
  // Config shape expected in ConfigAccessButton.js:
  //   "Disease Advisory": {
  //     "Categories Button":     { add_category, edit, delete }
  //     "Sub Categories Button": { add_category, edit, delete }
  //     "FAQs Button":           { add_faqs, edit, delete }
  //   }
  //
  const diseaseAdvisoryConfig = buttonConfig["Disease Advisory"] || {};

  const categoriesConfig    = diseaseAdvisoryConfig["Categories Button"]     || {};
  const subCategoriesConfig = diseaseAdvisoryConfig["Sub Categories Button"] || {};
  const faqsConfig          = diseaseAdvisoryConfig["FAQs Button"]           || {};

  // canCat(action)    → permission for the Categories tab
  // canSubCat(action) → permission for the Sub-Categories tab
  // canFaq(action)    → permission for the FAQs tab
  const canCat    = (action) => !!categoriesConfig[action];
  const canSubCat = (action) => !!subCategoriesConfig[action];
  const canFaq    = (action) => !!faqsConfig[action];

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
   const [pagination, setPagination] = useState({
      current_page: 1,
      page_size: 10,
      total_items: 0,
      total_pages: 1,
      has_next: false,
      has_previous: false,
      next_page_number: null,
      previous_page_number: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [10, 25, 50, 100];
    

  // Loading state
  const [loading, setLoading] = useState(false);

  // Dropdown data
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingFaqs, setLoadingFaqs] = useState(false);

  // Category CRUD states
  const [categoryItems, setCategoryItems] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryEditingId, setCategoryEditingId] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: "", sequence: "" });
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryDeletingId, setCategoryDeletingId] = useState(null);

  // Subcategory CRUD states
  const [subcategoryItems, setSubcategoryItems] = useState([]);
  const [subcategoryDialogOpen, setSubcategoryDialogOpen] = useState(false);
  const [subcategoryEditingId, setSubcategoryEditingId] = useState(null);
  const [subcategoryFormData, setSubcategoryFormData] = useState({ name: "", category_id: "", sequence: "" });
  const [subcategorySaving, setSubcategorySaving] = useState(false);
  const [subcategoryDeletingId, setSubcategoryDeletingId] = useState(null);

  // FAQ Pagination & Search states
  const [faqPage, setFaqPage] = useState(1);
  const [faqPageSize, setFaqPageSize] = useState(10);
  const [faqTotalCount, setFaqTotalCount] = useState(0);
  const [faqSearchInput, setFaqSearchInput] = useState("");
  const [faqSearchTerm, setFaqSearchTerm] = useState("");
  const [faqHasSearched, setFaqHasSearched] = useState(false);

  // FAQ CRUD states
  const [faqItems, setFaqItems] = useState([]);
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [faqEditingId, setFaqEditingId] = useState(null);
  const [faqFormData, setFaqFormData] = useState({ question: "", category_id: "", subcategory_id: "" });
  const [faqSaving, setFaqSaving] = useState(false);
  const [faqDeletingId, setFaqDeletingId] = useState(null);
  
  // Subcategory Pagination states
  const [subcategoryPage, setSubcategoryPage] = useState(1);
  const [subcategoryPageSize, setSubcategoryPageSize] = useState(10);
  const [subcategoryTotalCount, setSubcategoryTotalCount] = useState(0);
  
  // Subcategory Search state
  const [subcategorySearchInput, setSubcategorySearchInput] = useState("");
  const [subcategorySearchTerm, setSubcategorySearchTerm] = useState("");
  const [subcategoryHasSearched, setSubcategoryHasSearched] = useState(false);

  // Add client-side filtered subcategories
  const [filteredSubcategoryItems, setFilteredSubcategoryItems] = useState([]);

  const tabsLoadedRef = useRef({ 0: false, 1: false, 2: false });

  useEffect(() => {
    setPageTitle("Disease Advisory");
  }, [setPageTitle]);

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentTab === 0) {
      if (!tabsLoadedRef.current[0]) {
        tabsLoadedRef.current[0] = true;
        fetchCategoryItems();
      }
    } else if (currentTab === 1) {
      if (!tabsLoadedRef.current[1]) {
        tabsLoadedRef.current[1] = true;
        if (subcategoryPage !== 1) {
          setSubcategoryPage(1);
        } else {
          fetchSubcategoryItems(1, subcategoryPageSize);
        }
      }
    } else if (currentTab === 2) {
      if (!tabsLoadedRef.current[2]) {
        tabsLoadedRef.current[2] = true;
        if (faqPage !== 1) {
          setFaqPage(1);
        } else {
          fetchFaqItems(1, faqPageSize);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  // Fetch FAQs when page or page size changes (only if on FAQ tab)
  useEffect(() => {
    if (currentTab === 2) {
      fetchFaqItems(faqPage, faqPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqPage, faqPageSize]);

  // Fetch subcategories when page or page size changes (only if on subcategory tab)
  useEffect(() => {
    if (currentTab === 1) {
      fetchSubcategoryItems(subcategoryPage, subcategoryPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcategoryPage, subcategoryPageSize]);

  // Fetch subcategories when applied search term changes (only if on subcategory tab)
  useEffect(() => {
    if (currentTab === 1) {
      fetchSubcategoryItems(1, subcategoryPageSize, subcategorySearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcategorySearchTerm]);

  // Reset to page 1 when FAQ search term changes
  useEffect(() => {
    if (faqSearchTerm && faqPage !== 1) {
      setFaqPage(1);
    }
  }, [faqSearchTerm, faqPage]);

  // Reset to page 1 when subcategory search term changes
  useEffect(() => {
    if (subcategorySearchTerm && subcategoryPage !== 1) {
      setSubcategoryPage(1);
    }
  }, [subcategorySearchTerm, subcategoryPage]);

  // Apply client-side filtering to subcategories
  useEffect(() => {
    if (subcategorySearchTerm) {
      const filtered = subcategoryItems.filter(item => 
        item.name?.toLowerCase().includes(subcategorySearchTerm.toLowerCase()) ||
        item.category?.name?.toLowerCase().includes(subcategorySearchTerm.toLowerCase()) ||
        item.category_name?.toLowerCase().includes(subcategorySearchTerm.toLowerCase())
      );
      setFilteredSubcategoryItems(filtered);
    } else {
      setFilteredSubcategoryItems(subcategoryItems);
    }
  }, [subcategoryItems, subcategorySearchTerm]);

  const userId = localStorage.getItem("user_id") || "1";

  // Fetch categories
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_categories}?place=disease_advisory&user_id=${userId}`
      );
      if (response?.success && response?.data) {
        const categoryList = Array.isArray(response.data)
          ? response.data
          : response.data.categories || response.data.Category || [];
        setCategories(categoryList.filter((cat) => !cat.is_deleted && cat.status === "Active"));
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
    if (courseTypeId) {
      filterCoursesByType(courseTypeId, value, pageSize);
    } else {
      fetchCourses(selectedCrop, null, value, pageSize);
    }
  };

  // Fetch subcategories by category
  const fetchSubcategories = useCallback(async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    setLoadingSubcategories(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_subcategories_by_category}?category_id=${categoryId}&user_id=${userId}`
      );
      if (response?.success && response?.data) {
        const subCategoryList = Array.isArray(response.data)
          ? response.data
          : response.data.subcategories || response.data.SubCategory || [];
        setSubcategories(subCategoryList);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.showError("Failed to load subcategories");
    } finally {
      setLoadingSubcategories(false);
    }
  }, [userId, toast]);

  // Fetch subcategories when category_id changes in FAQ form (for edit mode)
  useEffect(() => {
    if (faqDialogOpen && faqFormData.category_id) {
      fetchSubcategories(Number(faqFormData.category_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqDialogOpen, faqFormData.category_id]);

  // Fetch FAQs by category and subcategory
  const fetchFaqs = useCallback(async (categoryId, subCategoryId) => {
    if (!categoryId || !subCategoryId) {
      setFaqs([]);
      return;
    }
    setLoadingFaqs(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_disease_advisory_question_by_category}?category=${categoryId}&sub_category=${subCategoryId}`
      );
      if (response?.success && response?.data) {
        const faqList = Array.isArray(response.data)
          ? response.data
          : response.data.questions || response.data.DiseaseAdvisoryQuestion || [];
        setFaqs(faqList);
      } else {
        setFaqs([]);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      setFaqs([]);
    } finally {
      setLoadingFaqs(false);
    }
  }, []);

  // Fetch category items
  const fetchCategoryItems = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_categories}?place=disease_advisory&user_id=${userId}`
      );
      if (response?.success && response?.data) {
        const categoryList = Array.isArray(response.data)
          ? response.data
          : response.data.categories || response.data.Category || [];
        const sortedList = [...categoryList].sort((a, b) => {
          const seqA = a.sequence || 999;
          const seqB = b.sequence || 999;
          return seqA - seqB;
        });
        setCategoryItems(sortedList);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subcategory items
  const fetchSubcategoryItems = async (page = subcategoryPage, pageSize = subcategoryPageSize, search = subcategorySearchTerm) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: userId,
        page: String(page),
        page_size: String(pageSize)
      });
      if (search.trim()) {
        params.append('search', search.trim());
      }
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_all_subcategories}?${params.toString()}`
      );
      if (response?.success && response?.data) {
        const subCategoryList = Array.isArray(response.data)
          ? response.data
          : response.data.data || response.data.subcategories || response.data.SubCategory || [];
        setSubcategoryItems(subCategoryList);
        
        if (response.pagination && response.pagination.total_items !== undefined) {
          setSubcategoryTotalCount(response.pagination.total_items);
        } else if (response.data.count !== undefined) {
          setSubcategoryTotalCount(response.count);
        } else if (response.data.total !== undefined) {
          setSubcategoryTotalCount(response.total);
        } else if (response.data.total_count !== undefined) {
          setSubcategoryTotalCount(response.total_count);
        } else {
          if (subCategoryList.length === pageSize) {
            setSubcategoryTotalCount((page * pageSize) + 1);
          } else {
            setSubcategoryTotalCount((page - 1) * pageSize + subCategoryList.length);
          }
        }
      } else {
        setSubcategoryItems([]);
        setSubcategoryTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.showError("Failed to load subcategories");
      setSubcategoryItems([]);
      setSubcategoryTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch FAQ items
  const fetchFaqItems = async (page = 1, pageSize = faqPageSize, searchTerm = faqSearchTerm) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: userId,
        page: String(page),
        page_size: String(pageSize)
      });
      
      if (searchTerm && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_disease_advisory_question_by_category}?${params.toString()}`
      );

      if (response?.success && response?.data) {
        const faqList =
          response.data.data ||
          response.data.questions ||
          response.data.DiseaseAdvisoryQuestion ||
          [];

        setFaqItems(faqList);

        const total =
          response.data?.pagination?.total_items ||
          response.data?.total ||
          response.data?.count ||
          faqList.length;

        setFaqTotalCount(total);
      } else {
        setFaqItems([]);
        setFaqTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.showError("Failed to load FAQs");
      setFaqItems([]);
      setFaqTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Category CRUD functions
  const handleCategoryOpenDialog = (item = null) => {
    if (item) {
      setCategoryEditingId(item.id);
      setCategoryFormData({
        name: item.name || "",
        sequence: item.sequence || "",
      });
    } else {
      setCategoryEditingId(null);
      setCategoryFormData({ name: "", sequence: "" });
    }
    setCategoryDialogOpen(true);
  };

  const handleCategoryCloseDialog = () => {
    setCategoryDialogOpen(false);
    setCategoryEditingId(null);
    setCategoryFormData({ name: "", sequence: "" });
  };

  const handleCategorySave = async () => {
    if (!categoryFormData.name?.trim()) {
      toast.showError("Category name is required");
      return;
    }

    setCategorySaving(true);
    try {
      const formDataPayload = new FormData();
      formDataPayload.append('name', categoryFormData.name.trim());
      if (categoryFormData.sequence) {
        formDataPayload.append('sequence', categoryFormData.sequence);
      }
      formDataPayload.append('user_id', userId);

      let response;
      if (categoryEditingId) {
        formDataPayload.append('category_id', categoryEditingId);
        response = await apiService.put(API_ENDPOINTS.PUT_update_category, formDataPayload, undefined, 'multipart/form-data');
      } else {
        response = await apiService.post(API_ENDPOINTS.POST_add_category, formDataPayload, undefined, 'multipart/form-data');
      }

      if (response?.success) {
        toast.showSuccess(categoryEditingId ? "Category updated successfully" : "Category added successfully");
        handleCategoryCloseDialog();
        await fetchCategoryItems();
        await fetchCategories();
      } else {
        toast.showError(response?.message || "Failed to save category");
      }
    } catch (error) {
      console.error("Error saving category:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to save category";
      toast.showError(errorMessage);
    } finally {
      setCategorySaving(false);
    }
  };

  const handleCategoryDelete = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Category",
      message: "Are you sure you want to delete this category?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    setCategoryDeletingId(id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.DELETE_category}?category_id=${id}&user_id=${userId}`
      );
      if (response?.success) {
        toast.showSuccess("Category deleted successfully");
        fetchCategoryItems();
        fetchCategories();
      } else {
        toast.showError(response?.message || "Failed to delete category");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.showError("Failed to delete category");
    } finally {
      setCategoryDeletingId(null);
    }
  };

  // Subcategory CRUD functions
  const handleSubcategoryOpenDialog = (item = null) => {
    if (item) {
      setSubcategoryEditingId(item.id);
      setSubcategoryFormData({
        name: item.name || "",
        category_id: item.category_id || item.category?.id || "",
        sequence: item.sequence || "",
      });
    } else {
      setSubcategoryEditingId(null);
      setSubcategoryFormData({ name: "", category_id: "", sequence: "" });
    }
    setSubcategoryDialogOpen(true);
  };

  const handleSubcategoryCloseDialog = () => {
    setSubcategoryDialogOpen(false);
    setSubcategoryEditingId(null);
    setSubcategoryFormData({ name: "", category_id: "", sequence: "" });
  };

  const handleSubcategorySave = async () => {
    if (!subcategoryFormData.name?.trim() || !subcategoryFormData.category_id) {
      toast.showError("Subcategory name and category are required");
      return;
    }

    setSubcategorySaving(true);
    try {
      const formDataPayload = new FormData();
      formDataPayload.append('name', subcategoryFormData.name.trim());
      formDataPayload.append('category_id', subcategoryFormData.category_id);
      if (subcategoryFormData.sequence) {
        formDataPayload.append('sequence', subcategoryFormData.sequence);
      }
      formDataPayload.append('user_id', userId);

      let response;
      if (subcategoryEditingId) {
        formDataPayload.append('sub_category_id', subcategoryEditingId);
        response = await apiService.put(API_ENDPOINTS.PUT_update_subcategory, formDataPayload, undefined, 'multipart/form-data');
      } else {
        response = await apiService.post(API_ENDPOINTS.POST_add_subcategory, formDataPayload, undefined, 'multipart/form-data');
      }

      if (response?.success) {
        toast.showSuccess(subcategoryEditingId ? "Subcategory updated successfully" : "Subcategory added successfully");
        handleSubcategoryCloseDialog();
        fetchSubcategoryItems(subcategoryPage, subcategoryPageSize);
      } else {
        toast.showError(response?.message || "Failed to save subcategory");
      }
    } catch (error) {
      console.error("Error saving subcategory:", error);
      toast.showError("Failed to save subcategory");
    } finally {
      setSubcategorySaving(false);
    }
  };

  const handleSubcategoryDelete = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Subcategory",
      message: "Are you sure you want to delete this subcategory?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    setSubcategoryDeletingId(id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.DELETE_subcategory}?sub_category_id=${id}&user_id=${userId}`
      );
      if (response?.success) {
        toast.showSuccess("Subcategory deleted successfully");
        if (subcategoryItems.length === 1 && subcategoryPage > 1) {
          setSubcategoryPage(subcategoryPage - 1);
          fetchSubcategoryItems(subcategoryPage - 1, subcategoryPageSize);
        } else {
          fetchSubcategoryItems(subcategoryPage, subcategoryPageSize);
        }
      } else {
        toast.showError(response?.message || "Failed to delete subcategory");
      }
    } catch (error) {
      console.error("Error deleting subcategory:", error);
      toast.showError("Failed to delete subcategory");
    } finally {
      setSubcategoryDeletingId(null);
    }
  };

  // FAQ CRUD functions
  const handleFaqOpenDialog = async (item = null) => {
    if (item) {
      const categoryId = item.category ? String(item.category) : "";
      const subcategoryId = item.sub_category ? String(item.sub_category) : "";
      
      setFaqEditingId(item.id);
      setFaqFormData({
        question: item.question || item.prompt || "",
        category_id: categoryId,
        subcategory_id: subcategoryId,
      });
      
      await fetchCategories();
      
      if (categoryId) {
        await fetchSubcategories(Number(categoryId));
      }
    } else {
      setFaqEditingId(null);
      setFaqFormData({ question: "", category_id: "", subcategory_id: "" });
    }
    setFaqDialogOpen(true);
  };

  const handleFaqCloseDialog = () => {
    setFaqDialogOpen(false);
    setFaqEditingId(null);
    setFaqFormData({ question: "", category_id: "", subcategory_id: "" });
  };

  const handleFaqSave = async () => {
    if (!faqFormData.question?.trim() || !faqFormData.category_id || !faqFormData.subcategory_id) {
      toast.showError("Please fill all required fields");
      return;
    }

    setFaqSaving(true);
    try {
      const formDataPayload = new FormData();
      formDataPayload.append('category', Number(faqFormData.category_id));
      formDataPayload.append('sub_category', Number(faqFormData.subcategory_id));
      formDataPayload.append('question', faqFormData.question.trim());

      let response;
      if (faqEditingId) {
        formDataPayload.append('disease_advisory_question_id', faqEditingId);
        response = await apiService.put(
          API_ENDPOINTS.PUT_update_faq,
          formDataPayload,
          'multipart/form-data'
        );
        if (response?.success) {
          toast.showSuccess("FAQ updated successfully");
          handleFaqCloseDialog();
          await fetchFaqItems(faqPage, faqPageSize, faqSearchTerm);
        } else {
          toast.showError(response?.message || "Failed to update FAQ");
        }
      } else {
        response = await apiService.post(
          API_ENDPOINTS.POST_add_faq,
          formDataPayload,
          'multipart/form-data'
        );

        if (response?.success) {
          toast.showSuccess("FAQ added successfully");
          handleFaqCloseDialog();
          setFaqPage(1);
          await fetchFaqItems(1, faqPageSize, faqSearchTerm);
        } else {
          toast.showError(response?.message || "Failed to add FAQ");
        }
      }
    } catch (error) {
      console.error("Error saving FAQ:", error);
      toast.showError(error?.message || "Failed to save FAQ");
    } finally {
      setFaqSaving(false);
    }
  };

  const handleFaqDelete = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete FAQ",
      message: "Are you sure you want to delete this FAQ?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    setFaqDeletingId(id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.DELETE_faq_disease_advisory}?disease_advisory_question_id=${id}`
      );
      if (response?.success) {
        toast.showSuccess("FAQ deleted successfully");
        await fetchFaqItems(faqPage, faqPageSize, faqSearchTerm);
      } else {
        toast.showError(response?.message || "Failed to delete FAQ");
      }
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.showError(error?.message || "Failed to delete FAQ");
    } finally {
      setFaqDeletingId(null);
    }
  };

  // Search handlers for Subcategories
  const handleSubcategorySearch = () => {
    if (!subcategorySearchInput.trim()) {
      handleSubcategoryClearSearch();
      return;
    }
    setSubcategorySearchTerm(subcategorySearchInput);
    setSubcategoryPage(1);
    setSubcategoryHasSearched(true);
  };

  const handleSubcategoryClearSearch = () => {
    setSubcategorySearchInput("");
    setSubcategorySearchTerm("");
    setSubcategoryPage(1);
    setSubcategoryHasSearched(false);
  };

  const handleSubcategoryKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubcategorySearch();
    }
  };

  // Search handlers for FAQs
  const handleFaqSearch = () => {
    if (!faqSearchInput.trim()) {
      handleFaqClearSearch();
      return;
    }
    setFaqSearchTerm(faqSearchInput);
    setFaqPage(1);
    setFaqHasSearched(true);
    fetchFaqItems(1, faqPageSize, faqSearchInput);
  };

  const handleFaqClearSearch = () => {
    setFaqSearchInput("");
    setFaqSearchTerm("");
    setFaqPage(1);
    setFaqHasSearched(false);
    fetchFaqItems(1, faqPageSize, "");
  };

  const handleFaqKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleFaqSearch();
    }
  };

  // ─── RENDER: Category Tab ────────────────────────────────────────────────────
  // Permissions used:
  //   canCat('add_category') → "Add Category" button
  //   canCat('edit')         → Edit icon per row
  //   canCat('delete')       → Delete icon per row
  //   Modal Save button disabled when: !canCat('add_category') (adding) or !canCat('edit') (editing)
  const renderCategoryTab = () => (
    <>
      <HeaderCard>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Categories
          </Typography>
          <Muted variant="body2">Manage disease advisory categories</Muted>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* ── MODIFIED: "Add Category" button — visible but disabled when no permission ── */}
          <Tooltip title={canCat('add_category') ? "" : "No permission to add category"}>
            <span>
              <Button
                startIcon={<Add />}
                variant="contained"
                onClick={() => canCat('add_category') && handleCategoryOpenDialog()}
                disabled={!canCat('add_category')}
              >
                Add Category
              </Button>
            </span>
          </Tooltip>

          <Button startIcon={<Refresh />} variant="contained" onClick={fetchCategoryItems} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </HeaderCard>

      <TableCard>
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto", maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 20 }}>
              <TableRow>
                <StyledTableCell className="header" align="center" sx={{ width: '100px' }}>Sr No</StyledTableCell>
                <StyledTableCell className="header" align="center">Category Name</StyledTableCell>
                <StyledTableCell className="header" align="center">Sequence</StyledTableCell>
                <StyledTableCell className="header" align="center" sx={{ width: '150px' }}>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : categoryItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No categories found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                categoryItems.map((item, index) => (
                  <StyledTableRow key={item.id}>
                    <StyledTableCell align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {index + 1}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body1" fontWeight={500} sx={{ color: 'text.primary' }}>
                        {item.name}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {item.sequence || "-"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center" alignItems="center">

                        {/* ── MODIFIED: Edit icon — disabled when canCat('edit') is false ── */}
                        <Tooltip title={canCat('edit') ? "Edit" : "No permission to edit"}>
                          <span>
                            <IconButton
                              color="editGreen"
                              size="small"
                              onClick={() => canCat('edit') && handleCategoryOpenDialog(item)}
                              disabled={!canCat('edit')}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* ── MODIFIED: Delete icon — disabled when canCat('delete') is false ── */}
                        <Tooltip title={canCat('delete') ? "Delete" : "No permission to delete"}>
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => canCat('delete') && handleCategoryDelete(item.id)}
                              disabled={!canCat('delete') || categoryDeletingId === item.id}
                            >
                              {categoryDeletingId === item.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Delete fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>

                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>
    </>
  );

  // ─── RENDER: Subcategory Tab ─────────────────────────────────────────────────
  // Permissions used:
  //   canSubCat('add_category') → "Add Subcategory" button
  //   canSubCat('edit')         → Edit icon per row
  //   canSubCat('delete')       → Delete icon per row
  //   Modal Save button disabled when: !canSubCat('add_category') (adding) or !canSubCat('edit') (editing)
  const renderSubcategoryTab = () => {
    const totalPages = Math.ceil(subcategoryTotalCount / subcategoryPageSize);
    const startIndex = (subcategoryPage - 1) * subcategoryPageSize;
    const displayItems = filteredSubcategoryItems;

    return (
      <>
        <HeaderCard>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {/* <Typography variant="h6" fontWeight={700}>
              Subcategories
            </Typography> */}
            {/* <Muted variant="body2">Manage disease advisory subcategories</Muted> */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1, flex: 1, maxWidth: 600, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Search subcategories by name or category..."
              value={subcategorySearchInput}
              onChange={(e) => {
                const value = e.target.value;
                setSubcategorySearchInput(value);
                if (subcategoryHasSearched && value !== subcategorySearchTerm) {
                  setSubcategoryHasSearched(false);
                }
                if (!value.trim() && subcategorySearchTerm) {
                  handleSubcategoryClearSearch();
                }
              }}
              onKeyPress={handleSubcategoryKeyPress}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: subcategorySearchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSubcategoryClearSearch} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { height: 40 }
              }}
              sx={{ '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              onClick={handleSubcategorySearch}
              startIcon={<Search />}
              disabled={!subcategorySearchInput.trim() || (subcategorySearchInput === subcategorySearchTerm && subcategoryHasSearched)}
              sx={{ borderRadius: 2, textTransform: "none", height: 40, boxShadow: 'none', minWidth: 100 }}
            >
              Search
            </Button>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Rows</InputLabel>
              <Select
                value={subcategoryPageSize}
                label="Rows"
                onChange={handleSubcategoryPageSizeChange}
              >
                {[10, 25, 50, 100].map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* ── MODIFIED: "Add Subcategory" button — visible but disabled when no permission ── */}
            <Tooltip title={canSubCat('add_category') ? "" : "No permission to add subcategory"}>
              <span>
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  onClick={() => canSubCat('add_category') && handleSubcategoryOpenDialog()}
                  disabled={!canSubCat('add_category')}
                >
                  Add Subcategory
                </Button>
              </span>
            </Tooltip>

            <Button
              startIcon={<Refresh />}
              variant="contained"
              onClick={() => fetchSubcategoryItems(subcategoryPage, subcategoryPageSize)}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          </Box>
        </HeaderCard>

        {/* Search and Filters */}
        {/* <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1, flex: 1, maxWidth: 600 }}>
            <TextField
              fullWidth
              placeholder="Search subcategories by name or category..."
              value={subcategorySearchInput}
              onChange={(e) => {
                const value = e.target.value;
                setSubcategorySearchInput(value);
                if (subcategoryHasSearched && value !== subcategorySearchTerm) {
                  setSubcategoryHasSearched(false);
                }
                if (!value.trim() && subcategorySearchTerm) {
                  handleSubcategoryClearSearch();
                }
              }}
              onKeyPress={handleSubcategoryKeyPress}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: subcategorySearchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSubcategoryClearSearch} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { height: 40 }
              }}
              sx={{ '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              onClick={handleSubcategorySearch}
              startIcon={<Search />}
              disabled={!subcategorySearchInput.trim() || (subcategorySearchInput === subcategorySearchTerm && subcategoryHasSearched)}
              sx={{ borderRadius: 2, textTransform: "none", height: 40, boxShadow: 'none', minWidth: 100 }}
            >
              Search
            </Button>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Rows</InputLabel>
              <Select
                value={subcategoryPageSize}
                label="Rows"
                onChange={handleSubcategoryPageSizeChange}
              >
                {[10, 25, 50, 100].map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box> */}

        <TableCard>
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto", maxHeight: 700 }}>
            <Table stickyHeader>
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 20 }}>
                <TableRow>
                  <StyledTableCell className="header" align="center" sx={{ width: '100px' }}>Sr No</StyledTableCell>
                  <StyledTableCell className="header" align="center">Subcategory</StyledTableCell>
                  <StyledTableCell className="header" align="center">Category</StyledTableCell>
                  <StyledTableCell className="header" align="center" sx={{ width: '150px' }}>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : displayItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {subcategorySearchTerm ? "No subcategories found matching your search." : "No subcategories found."}
                      </Typography>
                      {subcategorySearchTerm && (
                        <Button variant="text" onClick={handleSubcategoryClearSearch} sx={{ mt: 1 }}>
                          Clear search
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayItems.map((item, index) => (
                    <StyledTableRow key={item.id}>
                      <StyledTableCell align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {startIndex + index + 1}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography variant="body1" fontWeight={500} sx={{ color: 'text.primary' }}>
                          {item.name}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography variant="body2">
                          {item.category?.name || item.category_name || "N/A"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center" alignItems="center">

                          {/* ── MODIFIED: Subcategory Edit icon ── */}
                          <Tooltip title={canSubCat('edit') ? "Edit" : "No permission to edit"}>
                            <span>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => canSubCat('edit') && handleSubcategoryOpenDialog(item)}
                                disabled={!canSubCat('edit')}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* ── MODIFIED: Subcategory Delete icon ── */}
                          <Tooltip title={canSubCat('delete') ? "Delete" : "No permission to delete"}>
                            <span>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => canSubCat('delete') && handleSubcategoryDelete(item.id)}
                                disabled={!canSubCat('delete') || subcategoryDeletingId === item.id}
                              >
                                {subcategoryDeletingId === item.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <Delete fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>

                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableCard>

        {/* Pagination */}
        {displayItems.length > 0 && (
          <Box display="flex" flexDirection="column" alignItems="center" gap={1} sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {subcategoryTotalCount > 0
                ? `Showing ${startIndex + 1} - ${Math.min(startIndex + displayItems.length, subcategoryTotalCount)} of ${subcategoryTotalCount}`
                : 'No subcategories'}
            </Typography>

            {totalPages > 1 && !subcategorySearchTerm && (
              <Stack alignItems="center">
                <AppPagination
                  shape="rounded"
                  count={totalPages}
                  page={subcategoryPage}
                  onChange={handleSubcategoryPageChange}
                />
              </Stack>
            )}
          </Box>
        )}
      </>
    );
  };

  // Handle FAQ page change
  const handleFaqPageChange = (event, value) => {
    setFaqPage(value);
    fetchFaqItems(value, faqPageSize, faqSearchTerm);
  };

  // Handle subcategory page change
  const handleSubcategoryPageChange = (event, value) => {
    setSubcategoryPage(value);
  };

  // Handle subcategory page size change
  const handleSubcategoryPageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setSubcategoryPageSize(newSize);
    setSubcategoryPage(1);
    setSubcategorySearchTerm("");
    setSubcategorySearchInput("");
    setSubcategoryHasSearched(false);
    fetchSubcategoryItems(1, newSize);
  };

  // Handle FAQ page size change
  const handleFaqPageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setFaqPageSize(newSize);
    setFaqPage(1);
    fetchFaqItems(1, newSize, faqSearchTerm);
  };

  // Filter FAQ items based on applied search term
  const filteredFaqItems = useMemo(() => {
    if (!faqSearchTerm.trim()) {
      return faqItems;
    }
    const query = faqSearchTerm.toLowerCase().trim();
    return faqItems.filter((item) => {
      const question = (item.question || item.prompt || "").toLowerCase();
      const category = (item.category_name || "").toLowerCase();
      const subcategory = (item.sub_category_name || "").toLowerCase();
      return (
        question.includes(query) ||
        category.includes(query) ||
        subcategory.includes(query)
      );
    });
  }, [faqItems, faqSearchTerm]);

  // ─── RENDER: FAQ Tab ─────────────────────────────────────────────────────────
  // Permissions used:
  //   canFaq('add_faqs') → "Add FAQ" button
  //   canFaq('edit')     → Edit icon per row
  //   canFaq('delete')   → Delete icon per row
  //   Modal Save button disabled when: !canFaq('add_faqs') (adding) or !canFaq('edit') (editing)
  const renderFaqTab = () => {
    const totalPages = Math.ceil(faqTotalCount / faqPageSize);
    const startIndex = (faqPage - 1) * faqPageSize;
    const displayItems = filteredFaqItems;

    return (
      <>
        <HeaderCard>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {/* <Typography variant="h6" fontWeight={700}>
              FAQs
            </Typography> */}
            {/* <Muted variant="body2">Manage frequently asked questions</Muted> */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1, flex: 1, maxWidth: 700, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Search by question, category, or subcategory..."
              value={faqSearchInput}
              onChange={(e) => {
                const value = e.target.value;
                setFaqSearchInput(value);
                if (faqHasSearched && value !== faqSearchTerm) {
                  setFaqHasSearched(false);
                }
                if (!value.trim() && faqSearchTerm) {
                  handleFaqClearSearch();
                }
              }}
              onKeyPress={handleFaqKeyPress}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: faqSearchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleFaqClearSearch} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { height: 40 }
              }}
              sx={{ '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              onClick={handleFaqSearch}
              startIcon={<Search />}
              disabled={!faqSearchInput.trim() || (faqSearchInput === faqSearchTerm && faqHasSearched)}
              sx={{ borderRadius: 2, textTransform: "none", height: 40, boxShadow: 'none', minWidth: 100 }}
            >
              Search
            </Button>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Rows</InputLabel>
              <Select value={faqPageSize} label="Rows" onChange={handleFaqPageSizeChange}>
                {[10, 25, 50, 100].map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* ── MODIFIED: "Add FAQ" button — visible but disabled when no permission ── */}
            <Tooltip title={canFaq('add_faqs') ? "" : "No permission to add FAQ"}>
              <span>
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  onClick={() => canFaq('add_faqs') && handleFaqOpenDialog()}
                  disabled={!canFaq('add_faqs')}
                >
                  Add FAQ
                </Button>
              </span>
            </Tooltip>

            <Button
              startIcon={<Refresh />}
              variant="contained"
              onClick={() => fetchFaqItems(faqPage, faqPageSize, faqSearchTerm)}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          </Box>
        </HeaderCard>
        

        <TableCard>
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto", maxHeight: 700 }}>
            <Table stickyHeader>
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 20 }}>
                <TableRow>
                  <StyledTableCell className="header">Sr No</StyledTableCell>
                  <StyledTableCell className="header">Question</StyledTableCell>
                  <StyledTableCell className="header">Category</StyledTableCell>
                  <StyledTableCell className="header">Subcategory</StyledTableCell>
                  <StyledTableCell className="header">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : displayItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {faqSearchTerm ? "No FAQs found matching your search." : "No FAQs found."}
                      </Typography>
                      {faqSearchTerm && (
                        <Button variant="text" onClick={handleFaqClearSearch} sx={{ mt: 1 }}>
                          Clear search
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayItems.map((item, index) => {
                    const originalIndex = faqItems.findIndex(faq => faq.id === item.id);
                    const displayIndex = originalIndex >= 0 ? originalIndex : index;
                    return (
                      <StyledTableRow key={item.id}>
                        <StyledTableCell>
                          <Chip
                            label={`${startIndex + displayIndex + 1}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography variant="body2" sx={{ maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {item.question || item.prompt || "N/A"}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography variant="body2">
                            {item.category_name || "N/A"}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography variant="body2">
                            {item.sub_category_name || "N/A"}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box display="flex" gap={1}>

                            {/* ── MODIFIED: FAQ Edit icon ── */}
                            <Tooltip title={canFaq('edit') ? "Edit" : "No permission to edit"}>
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={() => canFaq('edit') && handleFaqOpenDialog(item)}
                                  disabled={!canFaq('edit')}
                                >
                                  <Edit />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {/* ── MODIFIED: FAQ Delete icon ── */}
                            <Tooltip title={canFaq('delete') ? "Delete" : "No permission to delete"}>
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={() => canFaq('delete') && handleFaqDelete(item.id)}
                                  disabled={!canFaq('delete') || faqDeletingId === item.id}
                                >
                                  {faqDeletingId === item.id ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <Delete />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>

                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableCard>

        {/* Pagination */}
        {faqTotalCount > 0 && totalPages > 1 && !faqSearchTerm && (
          <Stack alignItems="center" marginY={3}>
            <AppPagination
              shape="rounded"
              count={totalPages}
              page={faqPage}
              onChange={handleFaqPageChange}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Showing {startIndex + 1} - {Math.min(startIndex + faqItems.length, faqTotalCount)} of {faqTotalCount} FAQs
            </Typography>
          </Stack>
        )}

        {faqSearchTerm && (
          <Stack alignItems="center" marginY={2}>
            <Typography variant="body2" color="text.secondary">
              {displayItems.length > 0
                ? `Found ${displayItems.length} FAQ${displayItems.length > 1 ? 's' : ''} matching "${faqSearchTerm}"`
                : `No FAQs found matching "${faqSearchTerm}"`
              }
            </Typography>
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="Categories" />
          <Tab label="Subcategories" />
          <Tab label="FAQs" />
        </Tabs>
      </Box>

      {currentTab === 0 && renderCategoryTab()}
      {currentTab === 1 && renderSubcategoryTab()}
      {currentTab === 2 && renderFaqTab()}

      {/* ── Category Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={categoryDialogOpen} onClose={handleCategoryCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {categoryEditingId ? "Edit Category" : "Add Category"}
            </Typography>
            <IconButton onClick={handleCategoryCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <TextField
              label="Category Name *"
              value={categoryFormData.name}
              onChange={(e) => setCategoryFormData((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="Sequence"
              type="number"
              value={categoryFormData.sequence}
              onChange={(e) => setCategoryFormData((prev) => ({ ...prev, sequence: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCategoryCloseDialog} disabled={categorySaving}>
            Cancel
          </Button>
          {/* ── MODIFIED: Modal Save — disabled by add_category permission (add) or edit permission (edit) ── */}
          <Button
            onClick={handleCategorySave}
            variant="contained"
            disabled={
              categorySaving ||
              !categoryFormData.name?.trim() ||
              (categoryEditingId ? !canCat('edit') : !canCat('add_category'))
            }
            startIcon={categorySaving ? <CircularProgress size={16} /> : <Save />}
          >
            {categorySaving ? "Saving..." : categoryEditingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Subcategory Dialog ───────────────────────────────────────────────── */}
      <Dialog open={subcategoryDialogOpen} onClose={handleSubcategoryCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {subcategoryEditingId ? "Edit Subcategory" : "Add Subcategory"}
            </Typography>
            <IconButton onClick={handleSubcategoryCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category *</InputLabel>
              <Select
                value={subcategoryFormData.category_id}
                onChange={(e) => setSubcategoryFormData((prev) => ({ ...prev, category_id: e.target.value }))}
                label="Category *"
                disabled={loadingCategories}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Subcategory Name *"
              value={subcategoryFormData.name}
              onChange={(e) => setSubcategoryFormData((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubcategoryCloseDialog} disabled={subcategorySaving}>
            Cancel
          </Button>
          {/* ── MODIFIED: Modal Save — disabled by add_category (add) or edit (edit) permission ── */}
          <Button
            onClick={handleSubcategorySave}
            variant="contained"
            disabled={
              subcategorySaving ||
              !subcategoryFormData.name?.trim() ||
              !subcategoryFormData.category_id ||
              (subcategoryEditingId ? !canSubCat('edit') : !canSubCat('add_category'))
            }
            startIcon={subcategorySaving ? <CircularProgress size={16} /> : <Save />}
          >
            {subcategorySaving ? "Saving..." : subcategoryEditingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── FAQ Dialog ───────────────────────────────────────────────────────── */}
      <Dialog open={faqDialogOpen} onClose={handleFaqCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {faqEditingId ? "Edit FAQ" : "Add FAQ"}
            </Typography>
            <IconButton onClick={handleFaqCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category *</InputLabel>
              <Select
                value={faqFormData.category_id || ""}
                onChange={(e) => {
                  const value = String(e.target.value);
                  setFaqFormData((prev) => {
                    if (prev.category_id === value) return prev;
                    const newData = { ...prev, category_id: value, subcategory_id: "" };
                    if (value) {
                      setTimeout(() => fetchSubcategories(Number(value)), 0);
                    }
                    return newData;
                  });
                }}
                label="Category *"
                disabled={loadingCategories}
              >
                <MenuItem value=""><em>Select Category</em></MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={String(category.id)}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Subcategory *</InputLabel>
              <Select
                value={faqFormData.subcategory_id || ""}
                onChange={(e) => setFaqFormData((prev) => ({ ...prev, subcategory_id: String(e.target.value) }))}
                label="Subcategory *"
                disabled={!faqFormData.category_id || loadingSubcategories}
              >
                <MenuItem value=""><em>Select Subcategory</em></MenuItem>
                {!faqFormData.category_id ? (
                  <MenuItem disabled>Please select a category first</MenuItem>
                ) : (
                  subcategories.map((subcategory) => (
                    <MenuItem key={subcategory.id} value={String(subcategory.id)}>
                      {subcategory.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <TextField
              label="Question *"
              multiline
              rows={4}
              value={faqFormData.question}
              onChange={(e) => setFaqFormData((prev) => ({ ...prev, question: e.target.value }))}
              fullWidth
              placeholder="Enter FAQ question..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFaqCloseDialog} disabled={faqSaving}>
            Cancel
          </Button>
          {/* ── MODIFIED: Modal Save — disabled by add_faqs (add) or edit (edit) permission ── */}
          <Button
            onClick={handleFaqSave}
            variant="contained"
            disabled={
              faqSaving ||
              !faqFormData.question?.trim() ||
              !faqFormData.category_id ||
              !faqFormData.subcategory_id ||
              (faqEditingId ? !canFaq('edit') : !canFaq('add_faqs'))
            }
            startIcon={faqSaving ? <CircularProgress size={16} /> : <Save />}
          >
            {faqSaving ? "Saving..." : faqEditingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default DiseaseAdvisory;